import { useState, useRef } from 'react';
import toast from 'react-hot-toast';

interface VoiceRecorderProps {
  patientId: string;
  patientName: string;
}

export default function VoiceRecorder({ patientId, patientName }: VoiceRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        await processAudio(audioBlob);

        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      toast.success('Recording started', {
        icon: '🎤',
        style: {
          borderRadius: '10px',
          background: '#1a9b8e',
          color: '#fff',
        },
      });
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      toast.success('Recording stopped. Processing...', {
        icon: '⏹️',
      });
    }
  };

  const processAudio = async (audioBlob: Blob) => {
    setIsProcessing(true);

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api-production-7df0.up.railway.app';

      // Step 1: Transcribe audio using Whisper
      toast.loading('Transcribing audio...', { id: 'processing' });

      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');

      const transcribeResponse = await fetch(`${apiUrl}/transcribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!transcribeResponse.ok) {
        throw new Error(`Transcription failed: ${transcribeResponse.statusText}`);
      }

      const { transcript, confidence } = await transcribeResponse.json();
      console.log('Transcription result:', { transcript, confidence });

      // Step 2: Generate SBAR from transcript
      toast.loading('Generating SBAR report...', { id: 'processing' });

      const sbarResponse = await fetch(`${apiUrl}/sbar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transcript,
          role: 'RN',
          patient_id: patientId,
          patient_context: {
            name: patientName,
          },
        }),
      });

      if (!sbarResponse.ok) {
        throw new Error(`SBAR generation failed: ${sbarResponse.statusText}`);
      }

      const { sbar, baseline_id, time_saved_seconds } = await sbarResponse.json();
      console.log('SBAR generated:', { sbar, baseline_id, time_saved_seconds });

      toast.success(`SBAR generated for ${patientName}! Saved ${Math.floor(time_saved_seconds / 60)} minutes`, {
        id: 'processing',
        icon: '✨',
        duration: 5000,
        style: {
          borderRadius: '10px',
          background: '#1a9b8e',
          color: '#fff',
        },
      });

      // Refresh the page to show the new SBAR
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      console.error('Error processing audio:', error);
      toast.error(`Failed to process audio: ${error instanceof Error ? error.message : 'Unknown error'}`, {
        id: 'processing',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
      <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
        <svg className="w-6 h-6 mr-2 text-peacockTeal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
        </svg>
        Voice Recording
      </h2>

      <div className="bg-gradient-to-br from-peacockTeal-50 to-phoenixGold-50 rounded-xl p-6 border-2 border-peacockTeal-200">
        {/* Recording Status */}
        <div className="text-center mb-6">
          {isRecording && (
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-4 h-4 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-lg font-bold text-gray-900">Recording... {formatTime(recordingTime)}</span>
            </div>
          )}

          {isProcessing && (
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-peacockTeal-500"></div>
              <span className="text-lg font-bold text-gray-900">Processing audio...</span>
            </div>
          )}

          {!isRecording && !isProcessing && (
            <p className="text-gray-700 mb-4">
              Click the microphone button below to start recording your clinical notes.
              Your voice will be transcribed and automatically converted into a structured SBAR report.
            </p>
          )}
        </div>

        {/* Recording Controls */}
        <div className="flex items-center justify-center gap-4">
          {!isRecording && !isProcessing && (
            <button
              onClick={startRecording}
              className="btn btn-primary flex items-center gap-3 px-8 py-4 text-lg"
            >
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
              Start Recording
            </button>
          )}

          {isRecording && (
            <button
              onClick={stopRecording}
              className="flex items-center gap-3 px-8 py-4 text-lg bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl shadow-lg transition-all"
            >
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
              </svg>
              Stop Recording
            </button>
          )}
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center text-sm text-gray-600">
          <p className="font-medium mb-2">💡 Tips for best results:</p>
          <ul className="text-left max-w-md mx-auto space-y-1">
            <li>• Speak clearly and at a moderate pace</li>
            <li>• Include patient situation, background, assessment, and recommendations</li>
            <li>• Mention any significant changes or concerns</li>
            <li>• Recording will be saved to patient record automatically</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
